import { createBaseApi } from '../baseApi';

// Create the API service with a unique reducer path
const baseApi = createBaseApi('messageApi');

// Extending the base API with additional tag types
const extendedApi = baseApi.enhanceEndpoints({
  addTagTypes: ['Chats', 'Messages', 'UnreadCount', 'FulfillmentGroup', 'Order']
});

export const messageApi = extendedApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all chats for the current user
    getChatList: builder.query({
      query: () => {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        return `/messages/chat/${userInfo.id || 'me'}`;
      },
      providesTags: ['Chats'],
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),
    
    // Get messages for a specific chat
    getChatMessages: builder.query({
      query: (params) => {
        if (params.senderId && params.receiverId) {
          return `/messages/conversation/${params.senderId}/${params.receiverId}`;
        }
        console.warn('getChatMessages called without senderId/receiverId');
        return '/messages/invalid-chat-params'; // Avoids an actual API call
      },
      transformResponse: (response, meta, args) => {
        return response.map(msg => ({
          ...msg,
          isSender: msg.messageSender && msg.messageSender.some(sender => sender.id === args.senderId)
        }));
      },
      providesTags: (result, error, params) => {
        if (params.senderId && params.receiverId) {
          return [{ type: 'Messages', id: [params.senderId, params.receiverId].sort().join('-') }];
        }
        return ['Messages'];
      },
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false, 
      refetchOnReconnect: false,
    }),
    
    // Send a new message
    sendMessage: builder.mutation({
      query: ({ receiverId, text, productId, senderId, fulfillmentGroupId, messageType }) => ({
        url: '/messages',
        method: 'POST',
        body: {
          text: text,
          messageReceiver: receiverId,
          messageSender: senderId,
          ...(productId && { product: productId }),
          ...(fulfillmentGroupId && { fulfillmentGroup: fulfillmentGroupId }),
          ...(messageType && { messageType })
        }
      }),
      invalidatesTags: (result, error, { senderId, receiverId }) => {
        const sId = String(senderId);
        const rId = String(receiverId);
        return [
          { type: 'Messages', id: [sId, rId].sort().join('-') },
          'Chats'
        ];
      }
    }),
    
    // Get unread message count
    getUnreadCount: builder.query({
      query: () => '/messages/unread/count',
      transformResponse: (response) => response.count || 0,
      providesTags: ['UnreadCount'],
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),
    
    // Mark messages as read
    markAsRead: builder.mutation({
      query: ({ messageIds }) => ({
        url: '/messages/mark-read',
        method: 'PUT',
        body: {
          ids: messageIds
        }
      }),
      invalidatesTags: [
        'UnreadCount',
        'Chats'
      ]
    }),

    // Make an offer on a product
    makeOffer: builder.mutation({
      query: ({ receiverId, productId, price, message, senderId }) => ({
        url: '/messages',
        method: 'POST',
        body: {
          messageReceiver: receiverId,
          messageSender: senderId,
          product: productId,
          offerPrice: price,
          messageText: message,
          messageType: 'offer'
        }
      }),
      invalidatesTags: (result, error, { senderId, receiverId }) => {
        const sId = String(senderId);
        const rId = String(receiverId);
        return [
          { type: 'Messages', id: [sId, rId].sort().join('-') },
          'Chats'
        ];
      }
    }),

    // Request a swap between items
    requestSwap: builder.mutation({
      query: ({ receiverId, myProductId, theirProductId, message, senderId }) => ({
        url: '/messages',
        method: 'POST',
        body: {
          messageReceiver: receiverId,
          messageSender: senderId,
          requestedProduct: theirProductId,
          offeredProduct: myProductId,
          messageText: message,
          messageType: 'swap'
        }
      }),
      invalidatesTags: (result, error, { senderId, receiverId }) => {
        const sId = String(senderId);
        const rId = String(receiverId);
        return [
          { type: 'Messages', id: [sId, rId].sort().join('-') },
          'Chats'
        ];
      }
    }),
    
    // Respond to an offer or swap request
    respondToRequest: builder.mutation({
      query: ({ messageId, accepted, responseMessage }) => ({
        url: `/messages/${messageId}`,
        method: 'PUT',
        body: {
          status: {
            responded: true,
            accepted,
            responseMessage
          }
        }
      }),
      invalidatesTags: (result) => [
        { type: 'Messages', id: result?.chatId || 'all' }, // Might need adjustment based on actual response
        'Chats'
      ]
    }),

    // Confirm pickup via chat
    confirmPickupViaChat: builder.mutation({
      query: ({ fulfillmentGroupId, code }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/confirm-pickup-via-chat`,
        method: 'POST',
        body: { code },
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        'Order',
        'Chats'
      ],
    }),
  })
});

export const {
  useGetChatListQuery,
  useGetChatMessagesQuery,
  useSendMessageMutation,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMakeOfferMutation,
  useRequestSwapMutation,
  useRespondToRequestMutation,
  useConfirmPickupViaChatMutation,
} = messageApi; 